<template>
    <gi-form
        v-if="form"
        ref="form"
        :form="form"
        :on-submit-fn="onSubmitFn"
        :history="history"
        :hide-actions="hideActions"
        @success="onSuccess"
        @error="onError"
        @form-error="onFormError"
        @load-history="onLoadHistory"
        @start-history-autosave="startHistoryAutosave"
        @stop-history-autosave="stopHistoryAutosave"
    >
        <template v-if="!hideHeader" #header>
            <app-page-header class="mb-4" />
        </template>
        <template #fields="{ loading }">
            <field-enum
                ref="workflow_status"
                v-model="form.workflow_status"
                :disabled="loading"
                :enums="OfferWorkflowStatusEnum"
                enum-a11y-key="enums.offer_workflow_status"
                :label="$t('workflow_status')"
            />
            <field-tax-status ref="tax_status" v-model="form.tax_status" :disabled="loading" required />
            <field-offer-status
                ref="status"
                v-model="form.status"
                :disabled="loading || !form.tax_status"
                :exclude="form.is_vct ? ['EVERGREEN'] : null"
                required
            />
            <field-offer-type ref="type" v-model="form.type" :disabled="loading" required />
            <field-string ref="name" v-model="form.name" :disabled="loading" required :label="$t('name')" />

            <field-description
                ref="overview"
                v-model="form.overview"
                :disabled="loading"
                :label="$t('overview')"
                wysiwyg
            />
            <field-description
                ref="max_fund_size_details"
                v-model="form.max_fund_size_details"
                :disabled="loading"
                label="Offer short description (summary table)"
                wysiwyg
            />
            <field-toggle
                ref="gi_app_form"
                v-model="form.gi_app_form"
                :disabled="loading"
                :label="$t('gi_app_form')"
                hide-details
                class="mb-4"
            />
            <!-- <field-toggle
                ref="prefilled_app_form"
                v-model="form.prefilled_app_form"
                :disabled="loading"
                :label="$t('prefilled_app_form')"
                hide-details
                class="mb-4"
            /> -->
            <!-- <field-toggle
                ref="offline_app_form"
                v-model="form.offline_app_form"
                :disabled="loading"
                :label="$t('offline_app_form')"
                hide-details
                class="mb-4"
            /> -->

            <field-toggle
                ref="in_custody"
                v-model="form.in_custody"
                :disabled="!form.is_single_company || loading"
                :label="$t('in_custody')"
                hide-details
                class="mb-4"
            />

            <field-toggle
                ref="provides_tax_relief_on_adviser_fees"
                v-model="form.provides_tax_relief_on_adviser_fees"
                :disabled="loading"
                :label="$t('provides_tax_relief_on_adviser_fees')"
                hide-details
                class="mb-4"
                :default-value="false"
            />

            <field-toggle
                ref="is_fund_offer"
                v-model="form.is_fund_offer"
                :disabled="loading || form.is_vct || form.is_iht"
                :label="$t('is_this_an_offer_for_a_fund')"
                hide-details
                class="mb-4"
            />
            <transition appear name="fade-transition" mode="out-in">
                <field-fund
                    v-if="form.is_fund_offer"
                    ref="fund_id"
                    v-model="form.fund_id"
                    allow-create
                    :disabled="loading"
                    required
                />
                <field-product-offers
                    v-else
                    ref="product_offers"
                    v-model="form.product_offers"
                    :disabled="loading"
                    :vct="form.is_vct"
                    required
                    @input:products="onProductsUpdated"
                    @input:has-single-product="onHasSingleProduct"
                />
            </transition>

            <template v-if="form.is_single_company">
                <field-toggle
                    ref="is_via_fund"
                    v-model="form.is_via_fund"
                    :disabled="loading"
                    :label="$t('is_this_investment_offered_via_a_fund')"
                    hide-details
                    class="mb-4"
                />

                <transition appear name="fade-transition" mode="out-in">
                    <field-fund
                        v-if="form.is_via_fund && form.is_single_company"
                        ref="via_fund_id"
                        v-model="form.via_fund_id"
                        :label="$t('via_fund')"
                        allow-create
                        :disabled="loading"
                        required
                    />
                </transition>
            </template>

            <app-tabs v-if="!setupLoading" v-model="tab" :items="tabs" />

            <v-tabs-items v-model="tab" class="is-transparent my-4 px-4">
                <v-tab-item key="key_information">
                    <h3 class="text-h5 mb-2">{{ $t('offer_details') }}</h3>

                    <field-description
                        ref="description"
                        v-model="form.description"
                        :disabled="loading"
                        :label="$t('description')"
                        wysiwyg
                        required
                    />

                    <template v-if="form.is_eis_or_seis_fund || form.is_iht || form.is_alt || form.is_pmi">
                        <field-money
                            ref="min_investment"
                            v-model="form.min_investment"
                            :disabled="loading"
                            :label="$t('min_investment')"
                            required
                        />
                        <field-description
                            ref="deadline_details"
                            v-model="form.deadline_details"
                            :disabled="loading"
                            :label="$t('deadline_details')"
                            wysiwyg
                        />
                        <field-datetime
                            ref="open_at"
                            v-model="form.open_at"
                            :disabled="loading"
                            :label="$t('open_at')"
                            preset-time="00:00:00"
                            required
                        />
                        <field-datetime
                            ref="close_at"
                            v-model="form.close_at"
                            :disabled="loading"
                            :label="$t('close_at')"
                            preset-time="00:00:00"
                            :required="form.status !== 'EVERGREEN'"
                        />
                        <field-datetime
                            ref="deadline_at"
                            v-model="form.deadline_at"
                            :disabled="loading"
                            :label="$t('next_deadline_date')"
                            preset-time="00:00:00"
                        />

                        <template v-if="form.is_single_company">
                            <template v-if="products">
                                <field-string
                                    v-for="product in products"
                                    :key="`${product.id}_share_class`"
                                    :value="product.share_class || ' '"
                                    :disabled="loading"
                                    :label="product.name + ' ' + $t('share_class')"
                                    readonly
                                />
                            </template>
                            <field-number
                                ref="shares_issued"
                                v-model="form.shares_issued"
                                :disabled="loading"
                                :label="$t('shares_issued')"
                            />
                            <field-number
                                ref="shares_offered"
                                v-model="form.shares_offered"
                                :disabled="loading"
                                :label="$t('shares_offered')"
                            />
                        </template>
                    </template>
                    <template v-else-if="form.is_vct">
                        <v-sheet outlined class="px-6 pt-4 mb-7">
                            <p>Next Offer Event</p>
                            <field-datetime
                                ref="open_at"
                                v-model="form.open_at"
                                :disabled="loading"
                                :label="$t('open_at')"
                                preset-time="00:00:00"
                                required
                            />
                            <field-datetime
                                ref="close_at"
                                v-model="form.close_at"
                                :disabled="loading"
                                :label="$t('close_at')"
                                preset-time="00:00:00"
                                :required="form.status !== 'EVERGREEN'"
                            />
                            <field-datetime
                                ref="next_allotment_at"
                                v-model="form.next_allotment_at"
                                :disabled="loading"
                                :label="$t('next_allotment_at')"
                                preset-time="00:00:00"
                            />
                            <field-datetime
                                ref="existing_investor_close_at"
                                v-model="form.existing_investor_close_at"
                                :disabled="loading"
                                :label="$t('existing_investor_close_at')"
                                preset-time="00:00:00"
                            />
                            <field-datetime
                                ref="early_bird_close_at"
                                v-model="form.early_bird_close_at"
                                :disabled="loading"
                                :label="$t('early_bird_close_at')"
                                preset-time="00:00:00"
                            />
                        </v-sheet>

                        <field-money
                            ref="min_investment"
                            v-model="form.min_investment"
                            :disabled="loading"
                            :label="$t('min_investment')"
                            required
                        />
                        <field-money
                            ref="max_size"
                            v-model="form.max_size"
                            :disabled="loading"
                            :label="$t('offer_size')"
                            required
                        />
                        <field-money
                            ref="min_size"
                            v-model="form.min_size"
                            :disabled="loading"
                            :label="$t('min_size')"
                        />
                        <field-description
                            ref="deadline_details"
                            v-model="form.deadline_details"
                            :disabled="loading"
                            :label="$t('deadline_details')"
                            wysiwyg
                        />
                        <field-datetime
                            ref="deadline_at"
                            v-model="form.deadline_at"
                            :disabled="loading"
                            :label="$t('next_deadline_date')"
                            preset-time="00:00:00"
                        />
                        <field-percentage
                            ref="early_bird_discount"
                            v-model="form.early_bird_discount"
                            :disabled="loading"
                            :label="$t('early_bird_discount')"
                        />
                        <field-datetime
                            ref="early_bird_close_at"
                            v-model="form.early_bird_close_at"
                            :disabled="loading"
                            :label="$t('early_bird_close_at')"
                            preset-time="00:00:00"
                        />
                        <field-string
                            ref="early_bird_offer"
                            v-model="form.early_bird_offer"
                            :disabled="loading"
                            :label="$t('early_bird_discount_description')"
                        />
                        <field-toggle
                            ref="over_allotment_available"
                            v-model="form.over_allotment_available"
                            :disabled="loading"
                            :label="$t('over_allotting')"
                            hide-details
                            class="mb-4"
                        />
                        <field-money
                            ref="over_allotment_proposed"
                            v-model="form.over_allotment_proposed"
                            :disabled="loading || !form.over_allotment_available"
                            :label="$t('over_allotment_proposed')"
                        />
                        <field-money
                            ref="over_allotment_actual"
                            v-model="form.over_allotment_actual"
                            :disabled="loading || !form.over_allotment_available"
                            :label="$t('over_allotment_actual')"
                        />
                        <field-money
                            ref="raised"
                            v-model="form.raised"
                            :disabled="loading"
                            :label="$t('funds_raised')"
                        />
                    </template>
                    <template v-else-if="form.is_single_company">
                        <field-datetime
                            ref="open_at"
                            v-model="form.open_at"
                            :disabled="loading"
                            :label="$t('open_at')"
                            preset-time="00:00:00"
                            required
                        />
                        <field-datetime
                            ref="close_at"
                            v-model="form.close_at"
                            :disabled="loading"
                            :label="$t('close_at')"
                            preset-time="00:00:00"
                            :required="form.status !== 'EVERGREEN'"
                        />
                        <field-money
                            ref="pre_money_valuation"
                            v-model="form.pre_money_valuation"
                            :disabled="loading"
                            :label="$t('pre_money_valuation')"
                        />
                        <field-money
                            ref="max_size"
                            v-model="form.max_size"
                            :disabled="loading"
                            :label="$t('max_size')"
                            required
                        />
                        <field-money
                            ref="min_investment"
                            v-model="form.min_investment"
                            :disabled="loading"
                            :label="$t('min_investment')"
                            required
                        />
                        <field-percentage
                            ref="equity_issued"
                            v-model="form.equity_issued"
                            :disabled="loading"
                            :label="$t('equity_issued')"
                        />
                        <field-lookup
                            ref="business_stage_id"
                            v-model="form.business_stage_id"
                            type="business-stage"
                            :disabled="loading"
                            :label="$t('business_stage')"
                            required
                        />
                        <field-datetime
                            ref="deadline_at"
                            v-model="form.deadline_at"
                            :disabled="loading"
                            :label="$t('next_deadline_date')"
                            preset-time="00:00:00"
                        />
                        <template v-if="products">
                            <field-string
                                v-for="product in products"
                                :key="`${product.id}_share_class`"
                                :value="product.share_class || ' '"
                                :disabled="loading"
                                :label="product.name + ' ' + $t('share_class')"
                                readonly
                            />
                        </template>
                        <field-number
                            ref="shares_issued"
                            v-model="form.shares_issued"
                            :disabled="loading"
                            :label="$t('shares_issued')"
                        />
                        <field-number
                            ref="shares_offered"
                            v-model="form.shares_offered"
                            :disabled="loading"
                            :label="$t('shares_offered')"
                        />
                    </template>
                    <template v-else>
                        <v-sheet outlined class="px-6 pt-4 mb-7">
                            <p>Next Offer Event</p>
                            <field-datetime
                                ref="open_at"
                                v-model="form.open_at"
                                :disabled="loading"
                                :label="$t('open_at')"
                                preset-time="00:00:00"
                                required
                            />
                            <field-datetime
                                ref="close_at"
                                v-model="form.close_at"
                                :disabled="loading"
                                :label="$t('close_at')"
                                preset-time="00:00:00"
                                :required="form.status !== 'EVERGREEN'"
                            />
                            <field-datetime
                                ref="next_allotment_at"
                                v-model="form.next_allotment_at"
                                :disabled="loading"
                                :label="$t('next_allotment_at')"
                                preset-time="00:00:00"
                            />
                            <field-datetime
                                ref="existing_investor_close_at"
                                v-model="form.existing_investor_close_at"
                                :disabled="loading"
                                :label="$t('existing_investor_close_at')"
                                preset-time="00:00:00"
                            />
                            <field-datetime
                                ref="early_bird_close_at"
                                v-model="form.early_bird_close_at"
                                :disabled="loading"
                                :label="$t('early_bird_close_at')"
                                preset-time="00:00:00"
                            />
                        </v-sheet>
                        <field-money
                            ref="min_investment"
                            v-model="form.min_investment"
                            :disabled="loading"
                            :label="$t('min_investment')"
                            required
                        />
                        <field-money
                            ref="max_size"
                            v-model="form.max_size"
                            :disabled="loading"
                            :label="$t('max_size')"
                            required
                        />
                        <field-toggle
                            ref="over_allotment_available"
                            v-model="form.over_allotment_available"
                            :disabled="loading"
                            :label="$t('over_allotting')"
                            hide-details
                            class="mb-4"
                        />
                        <field-money
                            ref="over_allotment_proposed"
                            v-model="form.over_allotment_proposed"
                            :disabled="loading || !form.over_allotment_available"
                            :label="$t('over_allotment_proposed')"
                        />
                        <field-money
                            ref="over_allotment_actual"
                            v-model="form.over_allotment_actual"
                            :disabled="loading || !form.over_allotment_available"
                            :label="$t('over_allotment_actual')"
                        />
                        <field-money
                            ref="raised"
                            v-model="form.raised"
                            :disabled="loading"
                            :label="$t('funds_raised')"
                        />
                    </template>

                    <template
                        v-if="
                            form.is_vct ||
                            (!form.is_single_company &&
                                !form.is_eis_or_seis_fund &&
                                !form.is_iht &&
                                !form.is_alt &&
                                !form.is_pmi)
                        "
                    >
                        <h3 class="text-h5 mb-2">{{ $t('income') }}</h3>

                        <field-enum
                            ref="target_dividend_type"
                            v-model="form.target_dividend_type"
                            :disabled="loading"
                            :enums="TargetDividendTypeEnum"
                            enum-a11y-key="enums.target_dividend_type"
                            :label="$t('target_dividend_type')"
                        />
                        <transition appear name="fade" mode="out-in">
                            <field-percentage
                                v-if="form.target_dividend_type === TargetDividendTypeEnum.YIELD"
                                ref="target_dividend"
                                v-model="form.target_dividend"
                                :disabled="loading"
                                :label="$t('target_annual_dividend')"
                            />
                            <field-number
                                v-else-if="form.target_dividend_type === TargetDividendTypeEnum.ABSOLUTE"
                                ref="target_dividend"
                                v-model="form.target_dividend"
                                :disabled="loading"
                                :label="$t('target_annual_dividend')"
                            >
                                <template #append>
                                    <small>{{ $t('p_per_share') }}</small>
                                </template>
                            </field-number>
                            <field-string v-else disabled :label="$t('target_annual_dividend')" />
                        </transition>
                    </template>

                    <h3 class="text-h5 mb-2">{{ $t('product') }}</h3>
                    <template v-if="form.is_eis_or_seis_fund || form.is_iht || form.is_alt || form.is_pmi">
                        <field-percentage
                            ref="target_return"
                            v-model="form.target_return"
                            :disabled="loading"
                            :label="$t('target_return')"
                        />
                        <field-description
                            ref="target_return_details"
                            v-model="form.target_return_details"
                            :disabled="loading"
                            :label="$t('target_return_details')"
                            wysiwyg
                        />
                    </template>
                    <template v-else-if="form.is_vct">
                        <template v-if="products">
                            <field-string
                                v-for="product in products"
                                :key="`${product.id}_share_class`"
                                :value="product.share_class || ' '"
                                :disabled="loading"
                                :label="product.name + ' ' + $t('share_class')"
                                readonly
                            />
                        </template>
                    </template>
                    <template v-else>
                        <field-string
                            ref="primary_exchange"
                            v-model="form.primary_exchange"
                            :disabled="loading"
                            :label="$t('primary_exchange')"
                        />
                        <field-string
                            ref="currency"
                            v-model="form.currency"
                            :disabled="loading"
                            :label="$t('currency')"
                        />
                    </template>
                </v-tab-item>

                <v-tab-item key="fund_overview">
                    <app-button
                        secondary
                        text="Populate Fund Overview"
                        :disabled="!form || !form.fund_id"
                        :loading="fundOverviewLoading"
                        class="d-block mb-2"
                        @click="onLoadFundOverview"
                    />
                    <field-description
                        ref="fund_overview"
                        v-model="form.fund_overview"
                        :disabled="loading"
                        :label="$t('fund_overview')"
                        wysiwyg
                        required
                    />
                </v-tab-item>

                <v-tab-item key="product_overview">
                    <field-toggle
                        v-if="form.has_multiple_products"
                        v-model="pullProductOverview"
                        label="Display an overview from each of the products?"
                        class="mt-2"
                    />
                    <app-button
                        v-else
                        secondary
                        text="Populate Product Overview"
                        :disabled="
                            !form ||
                            !form.product ||
                            !form.product.id ||
                            (pullProductOverview && form.has_multiple_products)
                        "
                        :loading="productOverviewLoading"
                        class="d-block mb-2"
                        @click="onLoadProductOverview"
                    />
                    <field-description
                        ref="product_overview"
                        v-model="form.product_overview"
                        :disabled="loading || (pullProductOverview && form.has_multiple_products)"
                        :label="$t('product_overview')"
                        wysiwyg
                    />
                </v-tab-item>

                <v-tab-item key="company_overview">
                    <offer-tab-company-overview :offer="form" />
                </v-tab-item>

                <v-tab-item key="manager_overview">
                    <app-button
                        secondary
                        text="Populate Fund Manager Overview"
                        :disabled="!form || !form.fund_id"
                        :loading="fundManagerOverviewLoading"
                        class="d-block mb-2"
                        @click="onLoadFundManagerOverview"
                    />
                    <field-description
                        ref="manager_overview"
                        v-model="form.fund_manager_details"
                        :disabled="loading"
                        :label="$t('manager_overview')"
                        wysiwyg
                        required
                    />
                </v-tab-item>

                <v-tab-item key="management_overview">
                    <field-description
                        ref="management_overview"
                        v-model="form.management_overview"
                        :disabled="loading"
                        :label="$t('management_overview')"
                        wysiwyg
                        required
                    />
                </v-tab-item>

                <v-tab-item key="application_form">
                    <v-input :label="$t('signatories')" hide-details />

                    <!-- eslint-disable max-len -->
                    <app-alert
                        alert-type="info"
                        text="The configuration of signatories can be modified here. Some Adobe roles (e.g. Signer) require setting up in the template, whereas some (such as Approver, Acceptor and Certified Recipient) do not. Note that Execution Only applications are overridden by the system to send to the Investor before the Adviser."
                        style="max-width: 68rem"
                        class="mt-1 mb-4"
                    />
                    <!-- eslint-enable max-len -->

                    <field-offer-signatories
                        v-model="form.application_form_required_signatures"
                        :disabled="loading"
                        :preset-items="presetOfferSignatories"
                    />

                    <field-enum
                        v-if="form.is_vct"
                        ref="available_redistribution"
                        v-model="form.available_redistribution"
                        multiple
                        :disabled="loading"
                        :readonly="hasSingleProduct"
                        :enums="ProposalRedistributionEnum"
                        enum-a11y-key="enums.proposal_redistribution"
                        enum-a11y-description-key="enums.proposal_redistribution_description"
                        :label="$t('available_redistribution')"
                        class="mt-6"
                    />
                    <field-description
                        ref="introduction_letter"
                        v-model="form.introduction_letter"
                        :disabled="loading"
                        :label="$t('introduction_letter')"
                        wysiwyg
                    />
                    <field-description
                        ref="terms_and_conditions"
                        v-model="form.terms_and_conditions"
                        :disabled="loading"
                        :label="form.is_vct ? $t('subscription_notice') : $t('additional_terms_and_conditions')"
                        wysiwyg
                    />
                    <field-description
                        ref="risk_warning"
                        v-model="form.risk_warning"
                        :disabled="loading"
                        :label="form.is_vct ? $t('declaration') : $t('risk_warning')"
                        wysiwyg
                    />
                    <field-percentage
                        ref="max_adviser_facilitated"
                        v-model="form.max_adviser_facilitated"
                        :disabled="loading"
                        :label="$t('max_adviser_facilitated')"
                    />
                    <field-percentage
                        ref="max_commission"
                        v-model="form.max_commission"
                        :disabled="loading"
                        :label="$t('max_commission')"
                    />
                </v-tab-item>

                <v-tab-item key="downloads">
                    <offer-tab-downloads :offer="form" />
                </v-tab-item>

                <v-tab-item key="fees">
                    <template v-if="form.is_vct">
                        <field-fee ref="fee_id" v-model="form.fee_id" :disabled="loading" required allow-create />
                        <field-percentage
                            ref="early_bird_discount"
                            v-model="form.early_bird_discount"
                            :disabled="loading"
                            :label="$t('early_bird_discount')"
                        />
                        <field-datetime
                            ref="early_bird_close_at"
                            v-model="form.early_bird_close_at"
                            :disabled="loading"
                            :label="$t('early_bird_close_at')"
                            preset-time="00:00:00"
                        />
                        <field-string
                            ref="early_bird_offer"
                            v-model="form.early_bird_offer"
                            :disabled="loading"
                            :label="$t('early_bird_discount_description')"
                        />
                        <field-percentage
                            ref="existing_shareholder_discount"
                            v-model="form.existing_shareholder_discount"
                            :disabled="loading"
                            :label="$t('existing_shareholder_discount')"
                        />
                        <field-datetime
                            ref="existing_investor_close_at"
                            v-model="form.existing_investor_close_at"
                            :disabled="loading"
                            :label="$t('existing_investor_close_at')"
                            preset-time="00:00:00"
                        />
                        <field-description
                            ref="existing_investor_close_description"
                            v-model="form.existing_investor_close_description"
                            :disabled="loading"
                            :label="$t('existing_investor_close_description')"
                            wysiwyg
                        />
                        <field-percentage
                            ref="growthinvest_discount"
                            v-model="form.growthinvest_discount"
                            :disabled="loading"
                            :label="$t('growthinvest_discount')"
                        />
                    </template>
                    <template v-else>
                        <field-fee
                            ref="fee_id"
                            v-model="form.fee_id"
                            :disabled="loading"
                            required
                            allow-create
                            class="mt-4"
                        />
                    </template>
                </v-tab-item>

                <v-tab-item key="company_details">
                    <offer-tab-company-details :offer="form" />
                </v-tab-item>

                <v-tab-item key="news">
                    <offer-tab-news :offer="form" />
                </v-tab-item>
            </v-tabs-items>
        </template>
        <template v-if="prependCancelButton" #prepend-actions>
            <app-button-cancel @click="onCancel" />
        </template>
    </gi-form>
</template>

<script>
import form from '@/mixins/form';
import history from '@/mixins/history';
import { Offer, Fund, FundManager, Product, ProductOffer } from '@/models';
import snack from '@/lib/helpers/snack';
import TargetDividendTypeEnum from '@/enums/targetDividendType';
import OfferWorkflowStatusEnum from '@/enums/offer/workflowStatus';
import ProposalRedistributionEnum from '@/enums/proposal/redistribution';

export default {
    name: 'CreateOffer',
    components: {
        GiForm: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/forms/Form'
            ),
        AppTabs: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/AppTabs'
            ),
        FieldOfferType: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/OfferType'
            ),
        FieldOfferStatus: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/OfferStatus'
            ),
        FieldTaxStatus: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/TaxStatus'
            ),
        FieldFund: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Fund'
            ),
        FieldProductOffers: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/ProductOffers'
            ),
        FieldFee: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/Fee'
            ),
        FieldOfferSignatories: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/components/fields/OfferSignatories'
            ),
        OfferTabCompanyDetails: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/views/offer/base/TabCompanyDetails.vue'
            ),
        OfferTabCompanyOverview: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/views/offer/base/TabCompanyOverview.vue'
            ),
        OfferTabDownloads: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/views/offer/base/TabDownloads.vue'
            ),
        OfferTabNews: () =>
            import(
                /* webpackChunkName: "data-entry" */
                '@/views/offer/base/TabNews.vue'
            )
    },
    mixins: [form, history],
    data: () => ({
        id: 'CreateOffer',
        model: Offer,
        tab: null,
        fundOverviewLoading: false,
        fundManagerOverviewLoading: false,
        productOverviewLoading: false,
        pullProductOverview: null,
        products: [],
        hasSingleProduct: null,
        //
        TargetDividendTypeEnum,
        OfferWorkflowStatusEnum,
        ProposalRedistributionEnum
    }),
    computed: {
        tabs() {
            return [
                {
                    id: 'key_information',
                    name: this.$t('key_information'),
                    badge: null
                },
                {
                    id: 'fund_overview',
                    name: this.$t('fund_overview'),
                    badge: null,
                    enabled:
                        this.form.is_alt_fund ||
                        this.form.is_pmi_fund ||
                        this.form.is_eis_fund ||
                        this.form.is_iht ||
                        this.form.is_seis_fund ||
                        this.form.is_non_qualifying_fund ||
                        this.form.is_hybrid_fund
                },
                {
                    id: 'product_overview',
                    name: this.$t('product_overview'),
                    badge: null,
                    enabled:
                        this.form.is_alt_single_company ||
                        this.form.is_pmi_single_company ||
                        this.form.is_eis_single_company ||
                        this.form.is_seis_single_company ||
                        this.form.is_vct ||
                        this.form.is_non_qualifying_single_company ||
                        this.form.is_hybrid_single_company
                },
                {
                    id: 'company_overview',
                    name: this.$t('company_overview'),
                    badge: null,
                    enabled:
                        this.form.is_alt_single_company ||
                        this.form.is_pmi_single_company ||
                        this.form.is_eis_single_company ||
                        this.form.is_seis_single_company ||
                        this.form.is_non_qualifying_single_company ||
                        this.form.is_hybrid_single_company
                },
                {
                    id: 'manager_overview',
                    name: this.$t('manager_overview'),
                    badge: null,
                    enabled:
                        this.form.is_alt_fund ||
                        this.form.is_pmi_fund ||
                        this.form.is_eis_fund ||
                        this.form.is_iht ||
                        this.form.is_seis_fund ||
                        this.form.is_non_qualifying_fund ||
                        this.form.is_hybrid_fund
                },
                {
                    id: 'management_overview',
                    name: this.$t('management_overview'),
                    badge: null,
                    enabled:
                        this.form.is_alt_single_company ||
                        this.form.is_pmi_single_company ||
                        this.form.is_eis_single_company ||
                        this.form.is_seis_single_company ||
                        this.form.is_vct ||
                        this.form.is_non_qualifying_single_company ||
                        this.form.is_hybrid_single_company
                },
                {
                    id: 'application_form',
                    name: this.$t('application_form'),
                    badge: null,
                    enabled: true
                },
                {
                    id: 'downloads',
                    name: this.$t('downloads'),
                    badge: null
                },
                {
                    id: 'fees',
                    name: this.$t('fees'),
                    badge: null
                },
                {
                    id: 'company_details',
                    name: this.$t('company_details'),
                    badge: null
                },
                {
                    id: 'news',
                    name: this.$t('news'),
                    badge: null
                }
            ];
        },
        presetOfferSignatories() {
            if (this.form.is_vct) {
                return [
                    {
                        signature_key: 'adviser',
                        order: 1,
                        type: 'ADVISER',
                        adobesign_application_role: 'SIGNER'
                    },
                    {
                        signature_key: 'investor',
                        order: 2,
                        type: 'INVESTOR',
                        adobesign_application_role: 'SIGNER'
                    }
                ];
            } else if (this.form.is_single_company) {
                return [
                    {
                        signature_key: 'investor',
                        order: 1,
                        type: 'INVESTOR',
                        adobesign_application_role: 'SIGNER'
                    }
                ];
            }

            return [];
        }
    },
    watch: {
        'form.is_fund_offer': {
            deep: true,
            handler(v) {
                if (!this.form) {
                    return;
                }

                if (v) {
                    this.form.in_custody = false;
                    this.form.product_offers = [];
                    this.form.is_via_fund = false;
                } else {
                    this.form.fund_id = null;
                }

                this.setSingleCompany();
            }
        },
        'form.is_via_fund': {
            handler(v) {
                if (!this.form) {
                    return;
                }

                if (!v) {
                    this.form.via_fund_id = null;
                    this.form.via_fund = null;
                }
            }
        },
        'form.tax_status'() {
            if (!this.form) {
                return;
            }
            this.setSingleCompany();
            this.setFundOffer();
        },
        'form.over_allotment_available'(available) {
            if (!this.form) {
                return;
            }
            if (!available) {
                this.form.over_allotment_proposed = null;
                this.form.over_allotment_actual = null;
            }
        },
        'form.target_dividend_type'(value, previousValue) {
            if (!this.form) {
                return;
            }
            if (value && previousValue) {
                this.form.target_dividend = null;
            }
        },
        pullProductOverview(value, previousValue) {
            if (!this.form || previousValue === null) {
                return;
            }
            if (value) {
                this.form.product_overview = null;
            } else {
                this.form.product_overview = {
                    content: [],
                    type: 'doc'
                };
            }
        }
    },
    methods: {
        async afterReset() {
            await this.afterSetup();
        },
        async afterSetup() {
            if (!this.form) {
                return;
            }

            if (this.form.has_fund) {
                this.form.is_fund_offer = true;
            }

            if (this.form.via_fund_id) {
                this.form.is_via_fund = true;
            }

            this.pullProductOverview = !this.form.product_overview;
        },
        setSingleCompany() {
            if (!this.form) {
                return;
            }

            if (this.form.is_fund || this.form.is_vct) {
                this.form.single_company = false;
            } else {
                this.form.single_company = true;
            }
        },
        setFundOffer() {
            if (!this.form) {
                return;
            }

            if (this.form.is_vct) {
                this.form.is_fund_offer = false;
            } else if (this.form.is_iht) {
                this.form.is_fund_offer = true;
            }
        },
        async onLoadFundOverview() {
            if (!this.form || !this.form.fund_id) {
                return false;
            }

            this.fundOverviewLoading = true;

            try {
                const fund = await Fund.$get(this.form.fund_id);
                if (fund.overview) {
                    this.form.fund_overview = fund.overview;
                } else {
                    snack({
                        timeout: 20000,
                        type: 'info',
                        text: 'Fund Overview is not available or empty'
                    });
                }
            } catch (error) {
                this.handleError(error, () => this.onLoadFundOverview());
            }

            this.fundOverviewLoading = false;
        },
        async onLoadFundManagerOverview() {
            if (!this.form || !this.form.fund_id) {
                return false;
            }

            this.fundManagerOverviewLoading = true;

            try {
                const fund = await Fund.$get(this.form.fund_id);

                if (fund.fund_manager_id) {
                    const fundManager = await FundManager.$get(fund.fund_manager_id);
                    if (fundManager.overview) {
                        this.form.fund_manager_details = fundManager.overview;
                    } else {
                        snack({
                            timeout: 20000,
                            type: 'info',
                            text: 'Fund Manager Overview is not available or empty'
                        });
                    }
                } else {
                    snack({
                        timeout: 20000,
                        type: 'info',
                        text: 'Fund is not available or empty'
                    });
                }
            } catch (error) {
                this.handleError(error, () => this.onLoadFundOverview());
            }

            this.fundManagerOverviewLoading = false;
        },
        async onLoadProductOverview() {
            if (!this.form || !this.form.product || !this.form.product.id) {
                return false;
            }

            this.productOverviewLoading = true;

            try {
                const product = await Product.$get(this.form.product.id);
                if (product.description) {
                    this.form.product_overview = product.description;
                } else {
                    snack({
                        timeout: 20000,
                        type: 'info',
                        text: 'Product Overview is not available or empty'
                    });
                }
            } catch (error) {
                this.handleError(error, () => this.onLoadProductOverview());
            }

            this.productOverviewLoading = false;
        },
        onProductsUpdated(products) {
            this.products = products;
        },
        onHasSingleProduct(hasSingleProduct) {
            this.hasSingleProduct = hasSingleProduct;

            if (hasSingleProduct) {
                this.form.available_redistribution = null;
            }
        },
        onDeleteModel() {
            ProductOffer.delete(record => record.offer_id === this.model_id);

            this.model.delete(this.model_id);
        }
    }
};
</script>
